package ui.app.assign


import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import api.AppType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Animation_play
import compose.assets.svg.Device
import compose.assets.svg.Link_variant
import compose.assets.svg.Plus
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_AppDetailUI
import tk.mallumo.compose.navigation.navTo_AppKindergartenPreviewUI
import tk.mallumo.compose.navigation.navTo_AppPreviewWheelUI
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.findRoot
import tools.rememberLazyGridState
import tools.rememberString
import ui.app.assign.detail.AppDetailArgs
import ui.widget.*

data class AppAssignListingArgs(var companyId: Long = -1)


private const val ITEM_SIZE = 384
private const val ITEM_ICON_SIZE = 64

@ComposableNavNode(AppAssignListingArgs::class)
@Composable
fun AppAssignListingUI() {
    with(AppAssignListingScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun AppAssignListingScope.Content(nnvl: AppAssignListingScope.Wrapper) {

    val variantPickerState = DialogState.remember()
    DialogVariantPicker(variantPickerState)

    Column(
        Modifier.fillMaxSize()
            .padding(top = 8.dp)
    ) {
        Box(Modifier.fillMaxWidth().padding(horizontal = 8.dp)) {
            HomeSearchUI(vm.searchFiled, label = Res.string.layer_bottom_listing__search)
        }
        val scrollState = nav.rememberLazyGridState("apps-assign-${args.companyId}")
        Box(Modifier.fillMaxWidth().weight(1F)) {
            if (vm.variants.isEmpty()) {
                Text(
                    text = Res.string.layer_bottom_listing__disabled_all.rememberString(),
                    style = MaterialTheme.typography.displaySmall,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.Center)
                        .alpha(0.5F)
                        .padding(24.dp)
                )
            } else if (vm.tabActive.filter(nnvl.state).isEmpty()) {
                Text(
                    text = vm.tabActive.emptyLabel.rememberString(),
                    style = MaterialTheme.typography.displaySmall,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.Center)
                        .alpha(0.5F)
                        .padding(24.dp)
                )
            } else {
                LazyVerticalGrid(
                    state = scrollState,
                    columns = GridCells.Adaptive(ITEM_SIZE.dp),
                    contentPadding = PaddingValues(
                        start = 8.dp,
                        top = 8.dp,
                        end = 8.dp,
                        bottom = 64.dp
                    ),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    itemsIndexed(
                        SearchWrapper.use(
                            fields = arrayOf(Io.ListingApp.Item::name),
                            items = vm.tabActive.filter(nnvl.state),
                            query = vm.searchFiled,
                            sort = compareByDescending { it.data.id }
                        )) { _, item ->
                        Item(item)
                    }
                }
            }

            if (vm.variants.isNotEmpty() && nnvl.canAdd) {
                this@Column.AnimatedVisibility(
                    visible = vm.tabActive == AppAssignListingScope.Tab.Active,
                    modifier = Modifier.align(Alignment.BottomEnd),
                    enter = fadeIn() + expandIn(expandFrom = Alignment.Center),
                    exit = fadeOut() + shrinkOut(shrinkTowards = Alignment.Center)
                ) {
                    FloatingActionButton(
                        onClick = { variantPickerState.show() },
                        modifier = Modifier.padding(16.dp)
                    ) { Icon(Svg.Plus, null) }
                }
            }
        }
        if (vm.variants.isNotEmpty()) {
            TabRow(
                selectedTabIndex = vm.tabAll.indexOf(vm.tabActive),
                modifier = Modifier.fillMaxWidth(),
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow
            ) {
                vm.tabAll.forEach {
                    Tab(
                        selected = it == vm.tabActive,
                        onClick = { action(AppAssignListingVM.Action.ActivateTab(it)) },
                        unselectedContentColor = MaterialTheme.colorScheme.onBackground,
                        text = {
                            Column {
                                Badge(
                                    modifier = Modifier.align(Alignment.CenterHorizontally),
                                    containerColor = LocalContentColor.current
                                ) {
                                    Text(it.counter(nnvl.state).toString())
                                }
                                Text(it.label.rememberString())
                            }
                        })
                }
            }
        }
    }
}

@Composable
private fun AppAssignListingScope.Item(item: AppAssignListingScope.Wrapper.Item) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer)
    ) {
        Box {
            Row(
                Modifier
                    .fillMaxWidth()
                    .clickable {
                        val args = AppDetailArgs(args.companyId, item.data.id)

                        with(nav.findRoot()) {
                            when (AppType[item.data.type]) {
                                AppType.WHEEL -> navTo_AppPreviewWheelUI(args)
                                AppType.KINDERGARTEN -> navTo_AppKindergartenPreviewUI(args)
                                AppType.APP_GROUP,
                                AppType.BALOON,
                                AppType.CAMERA,
                                AppType.GALLERY,
                                AppType.IMAGE,
                                AppType.MAZE,
                                AppType.NONE,
                                AppType.PDF,
                                AppType.QUIZ,
                                AppType.RSS,
                                AppType.USB_PLAYER,
                                AppType.VIDEO,
                                AppType.VIDEO_ALPHA_LOOP,
                                AppType.WWW_EMBEDDED,
                                AppType.WWW_LINK -> navTo_AppDetailUI(args)
                            }
                        }
                    }) {
                LazyImage(
                    id = item.data.logo,
                    modifier = Modifier.padding(8.dp).size(ITEM_ICON_SIZE.dp),
                    alphaBackground = false
                )
                Column(
                    modifier = Modifier.weight(1F)
                        .padding(vertical = 8.dp)
                        .padding(end = 8.dp)
                ) {
                    Text(
                        text = item.data.name,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.bodyLarge,
                        modifier = Modifier.fillMaxWidth()
                    )
                    Text(
                        text = AppType[item.data.type].typeName().rememberString(),
                        overflow = TextOverflow.Ellipsis,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.fillMaxWidth()
                    )

                    if (item.data.scheduled) {
                        Text(
                            text = Res.string.layer_bottom_listing__item_date_from_to.rememberString(
                                item.data.start.toString(),
                                item.data.end.toString()
                            ),
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }

            if (item.data.connection.isNotEmpty()) {
                TooltipAppGroup(
                    item = item,
                    modifier = Modifier
                        .padding(2.dp)
                        .align(Alignment.TopEnd)
                        .clip(CircleShape)
                )
            }

            if (item.data.campaigns.isNotEmpty()) {
                TooltipCampaigns(
                    item = item,
                    modifier = Modifier
                        .padding(2.dp)
                        .align(Alignment.CenterEnd)
                        .clip(CircleShape)
                )
            }

            if (item.data.devices.isNotEmpty()) {
                TooltipDevices(
                    item = item,
                    modifier = Modifier
                        .padding(2.dp)
                        .align(Alignment.BottomEnd)
                        .clip(CircleShape)
                )
            }
        }
    }
}

@Composable
private fun AppAssignListingScope.DialogVariantPicker(state: DialogState) {
    Dialog(state) {
        DialogUI(
            Modifier.align(Alignment.Center),
            title = { Text(Res.string.layer_bottom_listing__new_title.rememberString()) },
            onDismissRequest = { state.hide() },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            }) {
            LazyVerticalGrid(
                columns = GridCells.Fixed(1),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                items(
                    vm.variants.sortedWith(
                        compareBy<Io.ListingAppVariant.Item> { AppType.RSS match it.type }
                            .thenBy { it.type })
                ) { item ->
                    VariantItem(item, state)
                }
            }
        }
    }
}

@Composable
private fun AppAssignListingScope.VariantItem(item: Io.ListingAppVariant.Item, state: DialogState) {
    Row(
        Modifier
            .clip(CardDefaults.shape)
            .background(MaterialTheme.colorScheme.surface, CardDefaults.shape)
            .clickable {
                action(AppAssignListingVM.Action.CreateApp(item))
                state.hide()
            }) {
        LazyImage(
            id = item.logo,
            alphaBackground = false,
            modifier = Modifier
                .padding(8.dp)
                .size(ITEM_ICON_SIZE.dp)
        )
        Column(
            Modifier.fillMaxWidth()
                .align(Alignment.CenterVertically)
        ) {
            Text(
                text = AppType[item.type].typeName().rememberString(),
                maxLines = 1,
                style = MaterialTheme.typography.titleMedium,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth()
            )
            Text(
                text = AppType[item.type].typeDescription()?.rememberString() ?: item.name,
                maxLines = 2,
                style = MaterialTheme.typography.labelMedium,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TooltipAppGroup(
    item: AppAssignListingScope.Wrapper.Item,
    modifier: Modifier,
) {
    Box(modifier) {
        TooltipBox(
            positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
            tooltip = {
                Card(
                    colors = CardDefaults.cardColors(
                        MaterialTheme.colorScheme.surface
                    )
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(8.dp)
                    ) {
                        item.data.connection.forEach {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                LazyImage(
                                    id = it.logo,
                                    modifier = Modifier.size(32.dp),
                                    alphaBackground = false
                                )
                                Text(
                                    text = it.name,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.width(128.dp)
                                        .align(Alignment.CenterVertically)
                                )
                            }
                        }
                    }
                }
            },
            state = CustomTooltipState.rememberState(),
            focusable = false,
            enableUserInput = item.data.connection.isNotEmpty(),
            content = {
                Icon(
                    imageVector = Svg.Link_variant,
                    contentDescription = null,
                    modifier = Modifier.padding(8.dp).size(18.dp)
                )
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TooltipCampaigns(
    item: AppAssignListingScope.Wrapper.Item,
    modifier: Modifier,
) {
    Box(modifier) {
        TooltipBox(
            positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
            tooltip = {
                Card(
                    colors = CardDefaults.cardColors(
                        MaterialTheme.colorScheme.surface
                    )
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(8.dp)
                    ) {
                        item.data.campaigns.forEach {
                            Text(
                                text = it.name,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.width(128.dp)
                            )
                        }
                    }
                }
            },
            state = CustomTooltipState.rememberState(),
            focusable = false,
            enableUserInput = item.data.connection.isNotEmpty(),
            content = {
                Icon(
                    imageVector = Svg.Animation_play,
                    contentDescription = null,
                    modifier = Modifier.padding(8.dp).size(18.dp)
                )
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TooltipDevices(
    item: AppAssignListingScope.Wrapper.Item,
    modifier: Modifier
) {
    Box(modifier) {
        TooltipBox(
            positionProvider = TooltipDefaults.rememberPlainTooltipPositionProvider(),
            tooltip = {
                Card(
                    colors = CardDefaults.cardColors(
                        MaterialTheme.colorScheme.surface
                    )
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.padding(8.dp)
                    ) {
                        item.data.devices.forEach {
                            Text(
                                text = it.name,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.width(128.dp)
                            )
                        }
                    }
                }
            },
            state = CustomTooltipState.rememberState(),
            focusable = false,
            enableUserInput = item.data.devices.isNotEmpty(),
            content = {
                Icon(
                    imageVector = Svg.Device,
                    contentDescription = null,
                    modifier = Modifier.padding(8.dp).size(18.dp)
                )
            }
        )
    }
}

fun AppType.typeName(): StringResource = when (this) {
    AppType.NONE -> Res.string.global_empty
    AppType.IMAGE -> Res.string.layer_bottom_variant__name_image
    AppType.PDF -> Res.string.layer_bottom_variant__name_doc
    AppType.VIDEO -> Res.string.layer_bottom_variant__name_video
    AppType.WWW_EMBEDDED -> Res.string.layer_bottom_variant__name_www_embedded
    AppType.WWW_LINK -> Res.string.layer_bottom_variant__name_www_link
    AppType.RSS -> Res.string.layer_bottom_variant__name_rss
    AppType.APP_GROUP -> Res.string.layer_bottom_variant__name_app_group
    AppType.BALOON -> Res.string.layer_bottom_variant__name_balloons
    AppType.QUIZ -> Res.string.layer_bottom_variant__name_questionnaire
    AppType.GALLERY -> Res.string.layer_bottom_variant__name_gallery
    AppType.WHEEL -> Res.string.layer_bottom_variant__name_app_wheel
    AppType.CAMERA -> Res.string.layer_bottom_variant__name_app_camera
    AppType.VIDEO_ALPHA_LOOP -> Res.string.layer_bottom_variant__name_video_alpha_loop
    AppType.MAZE -> Res.string.layer_bottom_variant__name_app_maze
    AppType.USB_PLAYER -> Res.string.layer_bottom_variant__name_usb_player
    AppType.KINDERGARTEN -> Res.string.layer_bottom_variant__name_kindergarten
}

fun AppType.typeDescription(): StringResource? = when (this) {
    AppType.NONE -> Res.string.global_empty
    AppType.IMAGE -> Res.string.layer_bottom_variant__desc_image
    AppType.PDF -> Res.string.layer_bottom_variant__desc_doc
    AppType.VIDEO -> Res.string.layer_bottom_variant__desc_video
    AppType.WWW_EMBEDDED -> Res.string.layer_bottom_variant__desc_www_embedded
    AppType.WWW_LINK -> Res.string.layer_bottom_variant__desc_www_link
    AppType.RSS -> null
    AppType.APP_GROUP -> Res.string.layer_bottom_variant__desc_app_group
    AppType.BALOON -> Res.string.layer_bottom_variant__desc_balloons
    AppType.QUIZ -> Res.string.layer_bottom_variant__desc_questionnaire
    AppType.GALLERY -> Res.string.layer_bottom_variant__desc_gallery
    AppType.WHEEL -> Res.string.layer_bottom_variant__desc_app_wheel
    AppType.CAMERA -> Res.string.layer_bottom_variant__desc_app_camera
    AppType.VIDEO_ALPHA_LOOP -> Res.string.layer_bottom_variant__desc_video_alpha_loop
    AppType.MAZE -> Res.string.layer_bottom_variant__desc_app_maze
    AppType.USB_PLAYER -> Res.string.layer_bottom_variant__desc_usb_player
    AppType.KINDERGARTEN -> Res.string.layer_bottom_variant__desc_kindergarten
}
